import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../common/Card';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getTeamLogo } from '../../utils/teamLogos';
import { formatLocalDate, parseDateInput, toLocalDateKey } from '../../utils/formatters';
import { 
  Users, 
  GameController, 
  ChartBar, 
  Fire, 
  FirstAidKit, 
  ChartLineUp,
  ListBullets,
  Basketball,
  Target,
  Star,
  Lock,
  Crown
} from '@phosphor-icons/react';

const TABS = [
  { id: 'lineups', Icon: Users, labelKey: 'tabs.lineups', color: 'text-sky-400' },
  { id: 'props', Icon: GameController, labelKey: 'tabs.props', color: 'text-purple-400' },
  { id: 'h2h', Icon: ChartBar, labelKey: 'tabs.h2h', color: 'text-emerald-400' },
  { id: 'form', Icon: Fire, labelKey: 'tabs.form', color: 'text-orange-400' },
  { id: 'injuries', Icon: FirstAidKit, labelKey: 'tabs.injuries', color: 'text-red-400' },
  { id: 'stats', Icon: ChartLineUp, labelKey: 'tabs.stats', color: 'text-cyan-400' },
];

const PROP_TYPES = [
  { id: 'all', Icon: ListBullets, labelKey: 'props.all' },
  { id: 'points', Icon: Basketball, labelKey: 'props.points' },
  { id: 'rebounds', icon: '🪣', labelKey: 'props.rebounds' },
  { id: 'assists', Icon: Target, labelKey: 'props.assists' },
  { id: 'threes', Icon: Fire, labelKey: 'props.threes' },
  { id: 'combo', Icon: Star, labelKey: 'props.combo' },
];

// propType 到 i18n key 的映射（用于显示翻译后的盘口类型）
const PROP_TYPE_I18N_MAP = {
  points: 'props.points',
  rebounds: 'props.rebounds',
  assists: 'props.assists',
  threes: 'props.threes',
  three_pointers_made: 'props.threes',
  pts_rebs_asts: 'props.combo',
  points_rebounds_assists: 'props.combo',
  points_rebounds: 'props.combo',
  points_assists: 'props.combo',
  rebounds_assists: 'props.combo',
  pts_rebs: 'props.combo',
  pts_asts: 'props.combo',
  rebs_asts: 'props.combo',
};

// 获取翻译后的 propType 名称
const getPropTypeLabel = (propType, t) => {
  const key = PROP_TYPE_I18N_MAP[propType?.toLowerCase()];
  return key ? t(key) : propType;
};

export function GameInfoTabs({
  market,
  gameData,
  playerProps,
  playerPropsLoading,
  playerPropsError,
  playerPropsAccess,
  onLoadPlayerProps,
  gameDetail,
  gameId,
  accessStatus,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('lineups');

  useEffect(() => {
    if (activeTab === 'props' && onLoadPlayerProps) {
      onLoadPlayerProps();
    }
  }, [activeTab, onLoadPlayerProps]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'lineups':
        return <LineupsTab gameData={gameData} gameId={gameId} market={market} t={t} />;
      case 'props':
        return (
          <PropsTab
            playerProps={playerProps}
            playerPropsLoading={playerPropsLoading}
            playerPropsError={playerPropsError}
            accessStatus={playerPropsAccess}
            user={user}
            t={t}
          />
        );
      case 'h2h':
        return <H2HTab market={market} gameDetail={gameDetail} t={t} />;
      case 'form':
        return <FormTab market={market} gameDetail={gameDetail} t={t} />;
      case 'injuries':
        return <InjuriesTab market={market} gameDetail={gameDetail} gameId={gameId} t={t} />;
      case 'stats':
        return <StatsTab market={market} gameDetail={gameDetail} accessStatus={accessStatus} user={user} t={t} />;
      default:
        return null;
    }
  };

  return (
    <Card>
      {/* Tab buttons */}
      <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-glass-border">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              if (tab.id === 'props' && onLoadPlayerProps) {
                onLoadPlayerProps();
              }
            }}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === tab.id
                ? 'bg-accent-strong text-white'
                : 'bg-surface-highlight/70 text-gray-400 hover:bg-surface-highlight hover:text-white'
            }`}
          >
            <tab.Icon size={18} weight="duotone" className={activeTab === tab.id ? '' : tab.color} />
            <span>{t(tab.labelKey)}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="min-h-[200px]">
        {renderTabContent()}
      </div>
    </Card>
  );
}

function LineupsTab({ gameData, gameId, market, t }) {
  const [lineupsData, setLineupsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gameStarted, setGameStarted] = useState(null); // null = unknown, true = started, false = not started

  // Fetch lineups data from ESPN boxscore API (only available after game starts)
  useEffect(() => {
    const fetchLineups = async () => {
      if (gameData?.lineups) {
        setLineupsData(gameData);
        setGameStarted(true);
        return;
      }
      if (!market?.teamA || !market?.teamB) return;
      
      setLoading(true);
      try {
        // Use getGameLineups API which fetches from ESPN boxscore (real starting lineups)
        // Pass eventId (gameId) to get real lineups if game has started
        const data = await api.getGameLineups(market.teamA, market.teamB, 'nba', gameId || '');
        
        // Check if game has started
        setGameStarted(data?.gameStarted ?? false);
        
        if (data?.lineups) {
          setLineupsData({
            homeTeam: { name: data.homeTeam?.name || market?.teamB },
            awayTeam: { name: data.awayTeam?.name || market?.teamA },
            lineups: {
              home: data.lineups.home || [],
              away: data.lineups.away || [],
            },
          });
        }
      } catch (err) {
        console.error('Failed to fetch lineups:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLineups();
  }, [gameData, market?.teamA, market?.teamB, gameId]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <Users size={48} weight="duotone" className="mx-auto mb-3 text-accent animate-pulse" />
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  if (!lineupsData?.lineups || (lineupsData.lineups.home?.length === 0 && lineupsData.lineups.away?.length === 0)) {
    // Show different message based on whether game has started
    const isGameNotStarted = gameStarted === false;
    return (
      <div className="text-center py-8">
        <Users size={48} weight="duotone" className="mx-auto mb-3 text-gray-500" />
        <div className="text-gray-400">
          {isGameNotStarted ? t('lineups.noData') : t('tabs.lineupsNotAvailable')}
        </div>
        <div className="text-sm text-gray-500 mt-2">
          {isGameNotStarted ? t('lineups.notStartedHint') : t('tabs.lineupsHint')}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Away team lineup */}
      <div>
        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-glass-border">
          <img
            src={getTeamLogo(lineupsData.awayTeam?.name || market?.teamA, 40)}
            alt={lineupsData.awayTeam?.name}
            className="w-10 h-10 object-contain"
            onError={(e) => { e.target.style.opacity = '0.3'; }}
          />
          <div>
            <h4 className="font-bold text-base uppercase tracking-wide text-accent-warm">
              {lineupsData.awayTeam?.name}
            </h4>
            <div className="text-xs text-gray-500">{t('lineups.starting5', '首发 5 人')}</div>
          </div>
        </div>
        <div className="space-y-1">
          {lineupsData.lineups?.away?.map((player, i) => (
            <div key={i} className="flex items-center justify-between py-3 px-2 rounded-lg hover:bg-surface-highlight/40 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-surface-highlight/60 ring-2 ring-glass-border flex items-center justify-center">
                  <img
                    src={player.headshot || `https://a.espncdn.com/combiner/i?img=/i/headshots/nophoto.png&w=96&h=70`}
                    alt={player.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { 
                      e.target.onerror = null;
                      e.target.src = 'https://a.espncdn.com/combiner/i?img=/i/headshots/nophoto.png&w=96&h=70';
                    }}
                  />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">{player.name}</div>
                  <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                    {player.position && <span className="text-accent-warm font-medium">{player.position}</span>}
                    {player.height && <span className="text-gray-500">{player.height}</span>}
                  </div>
                </div>
              </div>
              {player.jersey && (
                <div className="text-accent-warm text-2xl font-bold tabular-nums">#{player.jersey}</div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Home team lineup */}
      <div>
        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-glass-border">
          <img
            src={getTeamLogo(lineupsData.homeTeam?.name || market?.teamB, 40)}
            alt={lineupsData.homeTeam?.name}
            className="w-10 h-10 object-contain"
            onError={(e) => { e.target.style.opacity = '0.3'; }}
          />
          <div>
            <h4 className="font-bold text-base uppercase tracking-wide text-positive">
              {lineupsData.homeTeam?.name}
            </h4>
            <div className="text-xs text-gray-500">{t('lineups.starting5', '首发 5 人')}</div>
          </div>
        </div>
        <div className="space-y-1">
          {lineupsData.lineups?.home?.map((player, i) => (
            <div key={i} className="flex items-center justify-between py-3 px-2 rounded-lg hover:bg-surface-highlight/40 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-surface-highlight/60 ring-2 ring-glass-border flex items-center justify-center">
                  <img
                    src={player.headshot || `https://a.espncdn.com/combiner/i?img=/i/headshots/nophoto.png&w=96&h=70`}
                    alt={player.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { 
                      e.target.onerror = null;
                      e.target.src = 'https://a.espncdn.com/combiner/i?img=/i/headshots/nophoto.png&w=96&h=70';
                    }}
                  />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">{player.name}</div>
                  <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                    {player.position && <span className="text-positive font-medium">{player.position}</span>}
                    {player.height && <span className="text-gray-500">{player.height}</span>}
                  </div>
                </div>
              </div>
              {player.jersey && (
                <div className="text-positive text-2xl font-bold tabular-nums">#{player.jersey}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PropsTab({
  playerProps,
  playerPropsLoading,
  playerPropsError,
  accessStatus,
  user,
  t,
}) {
  const [propFilter, setPropFilter] = useState('all');
  const [expandedPlayers, setExpandedPlayers] = useState({});
  const [displayCount, setDisplayCount] = useState(10);
  const PAGE_SIZE = 10;

  // Reset display count when filter changes
  const handleFilterChange = (filterId) => {
    setPropFilter(filterId);
    setDisplayCount(PAGE_SIZE);
  };

  const isAuthRequired = accessStatus?.isAuthRequired;
  const isMembershipRequired = accessStatus?.isMembershipRequired;

  if (playerPropsLoading) {
    return (
      <div className="text-center py-8">
        <GameController size={48} weight="duotone" className="mx-auto mb-3 text-accent animate-pulse" />
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  if (!playerProps || playerProps.length === 0) {
    // Determine the appropriate message based on access status
    const emptyState = isAuthRequired
      ? { Icon: Lock, text: t('auth.loginRequired', '请先登录'), className: 'text-accent' }
      : isMembershipRequired
      ? { Icon: Crown, text: t('membership.upgradeRequired', '升级会员解锁球员数据盘口'), className: 'text-accent-warm' }
      : { Icon: GameController, text: t('tabs.propsNotAvailable'), className: 'text-gray-400' };
    
    return (
      <div className="text-center py-8">
        <emptyState.Icon size={48} weight="duotone" className={`mx-auto mb-3 ${emptyState.className}`} />
        <div className={emptyState.className}>{emptyState.text}</div>
        {isMembershipRequired && (
          <button 
            onClick={() => window.dispatchEvent(new CustomEvent('membership-required', { detail: { currentTier: user?.tier_name || 'free' } }))}
            className="mt-4 px-4 py-2 bg-accent-strong hover:bg-accent text-white rounded-lg text-sm transition-colors"
          >
            {t('membership.viewPlans', '查看会员方案')}
          </button>
        )}
        {playerPropsError && !isAuthRequired && !isMembershipRequired && (
          <div className="mt-3 text-xs text-gray-500">
            {t('tabs.propsNotAvailable')}
          </div>
        )}
      </div>
    );
  }

  // Filter props by type
  const filteredProps = propFilter === 'all' 
    ? playerProps 
    : playerProps.filter(p => {
        const pt = p.propType?.toLowerCase() || '';
        if (propFilter === 'points') return pt === 'points';
        if (propFilter === 'rebounds') return pt === 'rebounds';
        if (propFilter === 'assists') return pt === 'assists';
        if (propFilter === 'threes') return pt === 'threes' || pt === 'three_pointers_made';
        if (propFilter === 'combo') {
          // 复合盘：包含多个统计类型的组合
          return pt.includes('_') || pt.includes('+') || 
                 pt === 'pts_rebs_asts' || pt === 'points_rebounds_assists' ||
                 pt === 'points_rebounds' || pt === 'points_assists' || 
                 pt === 'rebounds_assists' || pt === 'pts_rebs' || 
                 pt === 'pts_asts' || pt === 'rebs_asts';
        }
        return true;
      });

  // Paginated props
  const displayedProps = filteredProps.slice(0, displayCount);
  const hasMore = displayCount < filteredProps.length;
  const remainingCount = filteredProps.length - displayCount;

  // Count unique players based on filtered props
  const uniquePlayers = new Set(filteredProps.map(p => p.playerId)).size;

  const toggleExpand = (playerId, propType) => {
    const key = `${playerId}-${propType}`;
    setExpandedPlayers(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const formatOdds = (odds) => {
    if (odds >= 0) return `+${odds}`;
    return odds.toString();
  };

  const loadMore = () => {
    setDisplayCount(prev => prev + PAGE_SIZE);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <GameController size={24} weight="duotone" className="text-accent" />
          <h3 className="font-semibold">{t('props.title')}</h3>
          <span className="text-sm text-gray-400">
            {t('props.propsCount', { count: filteredProps.length })} · {t('props.playersCount', { count: uniquePlayers })}
          </span>
        </div>
      </div>

      {/* Filter buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        {PROP_TYPES.map(type => (
          <button
            key={type.id}
            onClick={() => handleFilterChange(type.id)}
            className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 transition-colors ${
              propFilter === type.id
                ? 'bg-accent-strong text-white'
                : 'bg-surface-highlight/70 text-gray-400 hover:bg-surface-highlight'
            }`}
          >
            {type.Icon ? <type.Icon size={16} weight="duotone" /> : <span>{type.icon}</span>}
            <span>{t(type.labelKey)}</span>
          </button>
        ))}
      </div>

      {/* Props list */}
      <div className="space-y-2">
        {displayedProps.map((prop, idx) => {
          const key = `${prop.playerId}-${prop.propType}`;
          const isExpanded = expandedPlayers[key];
          
          return (
            <div key={idx} className="bg-surface-highlight/50 rounded-lg p-3">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => toggleExpand(prop.playerId, prop.propType)}
              >
                {/* Player info */}
                <div className="flex items-center gap-3">
                  <img 
                    src={prop.playerHeadshot} 
                    alt={prop.playerName}
                    className="w-10 h-10 rounded-full bg-surface-highlight/70"
                    onError={(e) => { e.target.src = 'https://a.espncdn.com/combiner/i?img=/i/headshots/nophoto.png&w=96&h=70'; }}
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{prop.playerName}</span>
                      <span className="text-xs text-gray-500">{prop.playerPosition}</span>
                      <img 
                        src={`https://a.espncdn.com/i/teamlogos/nba/500/${prop.playerTeam?.toLowerCase()}.png`}
                        alt={prop.playerTeam}
                        className="w-4 h-4"
                      />
                    </div>
                    <div className="text-sm text-gray-400">
                      {getPropTypeLabel(prop.propType, t)}
                    </div>
                  </div>
                </div>

                {/* Line and odds */}
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-accent">{prop.line}</div>
                    <div className="text-xs text-gray-500">{t('props.platforms', { count: prop.vendors })}</div>
                  </div>
                  <div className="flex gap-2">
                    <div className="text-center px-2 py-1 bg-positive/15 rounded">
                      <div className={`text-sm font-medium ${prop.over?.odds >= 0 ? 'text-positive' : 'text-gray-300'}`}>
                        {formatOdds(prop.over?.odds)}
                      </div>
                      <div className="text-xs text-gray-500">{t('props.over')}</div>
                    </div>
                    <div className="text-center px-2 py-1 bg-negative/15 rounded">
                      <div className={`text-sm font-medium ${prop.under?.odds >= 0 ? 'text-positive' : 'text-gray-300'}`}>
                        {formatOdds(prop.under?.odds)}
                      </div>
                      <div className="text-xs text-gray-500">{t('props.under')}</div>
                    </div>
                  </div>
                  <span className="text-gray-500">{isExpanded ? '▲' : '▼'}</span>
                </div>
              </div>

              {/* Expanded vendor details */}
              {isExpanded && prop.allVendors && (
                <div className="mt-3 pt-3 border-t border-glass-border">
                  <div className="grid grid-cols-4 gap-2 text-xs text-gray-500 mb-2">
                    <div>{t('props.platform')}</div>
                    <div className="text-center">{t('props.line')}</div>
                    <div className="text-center">{t('props.over')}</div>
                    <div className="text-center">{t('props.under')}</div>
                  </div>
                  {prop.allVendors.map((vendor, vIdx) => (
                    <div key={vIdx} className="grid grid-cols-4 gap-2 text-sm py-1">
                      <div className="capitalize text-gray-300">{vendor.vendor}</div>
                      <div className="text-center text-accent">{vendor.line}</div>
                      <div className={`text-center ${vendor.overOdds >= 0 ? 'text-positive' : 'text-gray-300'}`}>
                        {formatOdds(vendor.overOdds)}
                      </div>
                      <div className={`text-center ${vendor.underOdds >= 0 ? 'text-positive' : 'text-gray-300'}`}>
                        {formatOdds(vendor.underOdds)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Load more button */}
      {hasMore && (
        <button
          onClick={loadMore}
          className="w-full mt-4 py-3 bg-surface-highlight/70 hover:bg-surface-highlight text-gray-300 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
        >
          <span>{t('props.loadMore')}</span>
          <span className="text-gray-500">（{t('props.remaining', { count: remainingCount })}）</span>
        </button>
      )}
    </div>
  );
}

function H2HTab({ market, gameDetail, t }) {
  const [teamStats, setTeamStats] = useState({ teamA: null, teamB: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeamStats = async () => {
      if (!market?.teamA || !market?.teamB) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const [statsA, statsB] = await Promise.all([
          api.getTeamStats(market.teamA, 30).catch(() => null),
          api.getTeamStats(market.teamB, 30).catch(() => null),
        ]);
        setTeamStats({ teamA: statsA, teamB: statsB });
      } catch {
        // Ignore errors
      } finally {
        setLoading(false);
      }
    };

    fetchTeamStats();
  }, [market?.teamA, market?.teamB]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <ChartBar size={48} weight="duotone" className="mx-auto mb-3 text-accent animate-pulse" />
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  if (!teamStats.teamA?.recentGames?.length && !teamStats.teamB?.recentGames?.length) {
    return (
      <div className="text-center py-8">
        <ChartBar size={48} weight="duotone" className="mx-auto mb-3 text-gray-500" />
        <div className="text-gray-400">{t('tabs.h2hNotAvailable')}</div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatLocalDate(dateStr, { month: 'numeric', day: 'numeric' });
  };

  const renderRecentGames = (teamName, stats, isHome) => {
    const games = stats?.recentGames;
    if (!games || games.length === 0) return null;
    
    return (
      <div className="mb-6">
        <h4 className="font-medium mb-3 flex items-center gap-2">
          <span className={isHome ? 'text-accent-warm' : 'text-accent'}>{teamName}</span>
          <span className="text-sm text-gray-500">- {t('h2h.recentGames')}</span>
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 border-b border-glass-border">
                <th className="text-left py-2 px-2">{t('h2h.date')}</th>
                <th className="text-left py-2 px-2">{t('h2h.opponent')}</th>
                <th className="text-center py-2 px-2">{t('h2h.result')}</th>
                <th className="text-center py-2 px-2">{t('h2h.score')}</th>
              </tr>
            </thead>
            <tbody>
              {games.slice(0, 5).map((game, idx) => {
                const isTeamHome = game.home === teamName;
                const teamScore = isTeamHome ? game.homeScore : game.awayScore;
                const opponentScore = isTeamHome ? game.awayScore : game.homeScore;
                const opponent = isTeamHome ? game.away : game.home;
                const isWin = game.winner === teamName;
                
                return (
                  <tr key={idx} className="border-b border-glass-border">
                    <td className="py-2 px-2 text-gray-400">{formatDate(game.date)}</td>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{isTeamHome ? 'vs' : '@'}</span>
                        <span>{opponent}</span>
                      </div>
                    </td>
                    <td className="py-2 px-2 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${isWin ? 'bg-positive/15 text-positive' : 'bg-negative/15 text-negative'}`}>
                        {isWin ? 'W' : 'L'}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-center text-gray-300">
                      {teamScore} - {opponentScore}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {renderRecentGames(market?.teamA, teamStats.teamA, true)}
      {renderRecentGames(market?.teamB, teamStats.teamB, false)}
    </div>
  );
}

function FormTab({ market, gameDetail, t }) {
  const [teamStats, setTeamStats] = useState({ teamA: null, teamB: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeamStats = async () => {
      if (!market?.teamA || !market?.teamB) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const [statsA, statsB] = await Promise.all([
          api.getTeamStats(market.teamA, 30).catch(() => null),
          api.getTeamStats(market.teamB, 30).catch(() => null),
        ]);
        setTeamStats({ teamA: statsA, teamB: statsB });
      } catch {
        // Ignore errors
      } finally {
        setLoading(false);
      }
    };

    fetchTeamStats();
  }, [market?.teamA, market?.teamB]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <Fire size={48} weight="duotone" className="mx-auto mb-3 text-accent-warm animate-pulse" />
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  // 从 recentGames 计算战绩（按日期排序，最新的在前）
  const getFormFromGames = (stats) => {
    const games = stats?.recentGames;
    if (!games || games.length === 0) return [];
    // 按日期降序排序，确保最新的比赛在前
    const sortedGames = [...games].sort((a, b) => new Date(b.date) - new Date(a.date));
    return sortedGames.slice(0, 5).map(g => g.winner === stats.teamName ? 'W' : 'L');
  };

  const teamAForm = getFormFromGames(teamStats.teamA);
  const teamBForm = getFormFromGames(teamStats.teamB);

  if (teamAForm.length === 0 && teamBForm.length === 0) {
    return (
      <div className="text-center py-8">
        <Fire size={48} weight="duotone" className="mx-auto mb-3 text-gray-500" />
        <div className="text-gray-400">{t('tabs.formNotAvailable')}</div>
      </div>
    );
  }

  // 计算连胜/连败（从最新一场开始计算）
  const getStreak = (form) => {
    if (form.length === 0) return { type: '-', count: 0 };
    // form[0] 是最新的比赛结果
    const first = form[0];
    let count = 0;
    for (const r of form) {
      if (r === first) count++;
      else break;
    }
    return { type: first, count };
  };

  const teamAStreak = getStreak(teamAForm);
  const teamBStreak = getStreak(teamBForm);

  // 计算胜率环形图数据（使用赛季总胜场）
  const teamAWins = teamStats.teamA?.wins || 0;
  const teamBWins = teamStats.teamB?.wins || 0;
  
  const pieData = [
    { name: market?.teamA, value: teamAWins, color: '#6f8db0' },
    { name: market?.teamB, value: teamBWins, color: '#c49a5c' },
  ];

  // 构建得分趋势数据
  const buildScoreTrendData = () => {
    const gamesA = teamStats.teamA?.recentGames || [];
    const gamesB = teamStats.teamB?.recentGames || [];
    
    // 合并两队的比赛日期，取最近10场
    const allDates = new Set();
    gamesA.forEach((g) => {
      const key = toLocalDateKey(g.date);
      if (key) allDates.add(key);
    });
    gamesB.forEach((g) => {
      const key = toLocalDateKey(g.date);
      if (key) allDates.add(key);
    });
    
    const sortedDates = Array.from(allDates).sort().slice(-10);
    
    return sortedDates.map((dateKey) => {
      const gameA = gamesA.find((g) => toLocalDateKey(g.date) === dateKey);
      const gameB = gamesB.find((g) => toLocalDateKey(g.date) === dateKey);
      
      // 获取该队在这场比赛中的得分
      const getTeamScore = (game, teamName) => {
        if (!game) return null;
        if (game.home === teamName) return game.homeScore;
        if (game.away === teamName) return game.awayScore;
        return null;
      };
      
      const d = parseDateInput(dateKey);
      return {
        date: d ? formatLocalDate(d, { month: 'numeric', day: 'numeric' }) : '-',
        [market?.teamA]: getTeamScore(gameA, teamStats.teamA?.teamName),
        [market?.teamB]: getTeamScore(gameB, teamStats.teamB?.teamName),
      };
    });
  };

  const scoreTrendData = buildScoreTrendData();

  return (
    <div className="space-y-6">
      {/* 上半部分：近期走势 + 得分趋势 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 左侧：近期走势 */}
        <div className="bg-surface-highlight/30 rounded-lg p-4">
          <h4 className="font-medium mb-4 flex items-center gap-2">
            <Fire size={20} weight="duotone" className="text-accent-warm" />
            <span>{t('form.recentForm')}</span>
          </h4>
          
          {/* 两队战绩并排 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            {/* Team A */}
            <div>
              <div className="text-sm text-gray-400 mb-2">{market?.teamA}</div>
              <div className="flex gap-1">
                {/* 反转显示顺序，让最新的在最右边 */}
                {[...teamAForm].reverse().map((result, i) => (
                  <span
                    key={i}
                    className={`w-7 h-7 rounded flex items-center justify-center font-bold text-xs ${
                      result === 'W' ? 'bg-positive' : 'bg-negative'
                    }`}
                  >
                    {result}
                  </span>
                ))}
              </div>
            </div>
            {/* Team B */}
            <div>
              <div className="text-sm text-gray-400 mb-2">{market?.teamB}</div>
              <div className="flex gap-1">
                {/* 反转显示顺序，让最新的在最右边 */}
                {[...teamBForm].reverse().map((result, i) => (
                  <span
                    key={i}
                    className={`w-7 h-7 rounded flex items-center justify-center font-bold text-xs ${
                      result === 'W' ? 'bg-positive' : 'bg-negative'
                    }`}
                  >
                    {result}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* 胜率环形图 */}
          <div className="flex items-center justify-center">
            <div className="w-40 h-40">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={60}
                    dataKey="value"
                    stroke="none"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* 图例 */}
          <div className="flex justify-center gap-6 mt-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-accent"></span>
              <span className="text-gray-400">{market?.teamA}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-negative"></span>
              <span className="text-gray-400">{market?.teamB}</span>
            </div>
          </div>
        </div>

        {/* 右侧：得分趋势 */}
        <div className="bg-surface-highlight/30 rounded-lg p-4">
          <h4 className="font-medium mb-4 flex items-center gap-2">
            <ChartLineUp size={20} weight="duotone" className="text-accent" />
            <span>{t('form.scoreTrend')}</span>
          </h4>
          
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={scoreTrendData}>
                <XAxis 
                  dataKey="date" 
                  stroke="#6b7280" 
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis 
                  stroke="#6b7280" 
                  fontSize={12}
                  tickLine={false}
                  domain={['dataMin - 10', 'dataMax + 10']}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: '#9ca3af' }}
                />
                <Line 
                  type="monotone" 
                  dataKey={market?.teamA} 
                  stroke="#6f8db0" 
                  strokeWidth={2}
                  dot={{ fill: '#6f8db0', strokeWidth: 0, r: 4 }}
                  connectNulls
                />
                <Line 
                  type="monotone" 
                  dataKey={market?.teamB} 
                  stroke="#c49a5c" 
                  strokeWidth={2}
                  dot={{ fill: '#c49a5c', strokeWidth: 0, r: 4 }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          {/* 图例 */}
          <div className="flex justify-center gap-6 mt-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-accent"></span>
              <span className="text-gray-400">{market?.teamA}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-negative"></span>
              <span className="text-gray-400">{market?.teamB}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 下半部分：详细统计卡片 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Team A 卡片 */}
        <div className="bg-accent-warm/15 border border-accent-warm/30 rounded-lg p-4">
          <div className="font-medium mb-3">{market?.teamA}</div>
          <div className="text-xs text-gray-500 mb-3">{t('form.home')}</div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-500">{t('form.winRate')}</div>
              <div className={`text-lg font-bold ${(teamStats.teamA?.winRate || 0) >= 0.5 ? 'text-positive' : 'text-negative'}`}>
                {Math.round((teamStats.teamA?.winRate || 0) * 100)}%
              </div>
            </div>
            <div>
              <div className="text-gray-500">{t('form.streak')}</div>
              <div className={`text-lg font-bold ${teamAStreak.type === 'W' ? 'text-positive' : teamAStreak.type === 'L' ? 'text-negative' : 'text-gray-400'}`}>
                {teamAStreak.count > 0 ? `${teamAStreak.count}${teamAStreak.type === 'W' ? t('form.wins') : t('form.losses')}` : '-'}
              </div>
            </div>
            <div>
              <div className="text-gray-500">{t('form.record')}</div>
              <div className="text-gray-300">{teamStats.teamA?.wins || 0}W - {teamStats.teamA?.losses || 0}L</div>
            </div>
            <div>
              <div className="text-gray-500">{t('form.avgScore')}</div>
              <div className="text-gray-300">{teamStats.teamA?.avgScore?.toFixed(1) || '-'}</div>
            </div>
          </div>
        </div>

        {/* Team B 卡片 */}
        <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
          <div className="font-medium mb-3">{market?.teamB}</div>
          <div className="text-xs text-gray-500 mb-3">{t('form.away')}</div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-500">{t('form.winRate')}</div>
              <div className={`text-lg font-bold ${(teamStats.teamB?.winRate || 0) >= 0.5 ? 'text-positive' : 'text-negative'}`}>
                {Math.round((teamStats.teamB?.winRate || 0) * 100)}%
              </div>
            </div>
            <div>
              <div className="text-gray-500">{t('form.streak')}</div>
              <div className={`text-lg font-bold ${teamBStreak.type === 'W' ? 'text-positive' : teamBStreak.type === 'L' ? 'text-negative' : 'text-gray-400'}`}>
                {teamBStreak.count > 0 ? `${teamBStreak.count}${teamBStreak.type === 'W' ? t('form.wins') : t('form.losses')}` : '-'}
              </div>
            </div>
            <div>
              <div className="text-gray-500">{t('form.record')}</div>
              <div className="text-gray-300">{teamStats.teamB?.wins || 0}W - {teamStats.teamB?.losses || 0}L</div>
            </div>
            <div>
              <div className="text-gray-500">{t('form.avgScore')}</div>
              <div className="text-gray-300">{teamStats.teamB?.avgScore?.toFixed(1) || '-'}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InjuriesTab({ market, gameDetail, gameId, t }) {
  const [injuriesData, setInjuriesData] = useState([]);
  const [loading, setLoading] = useState(true);

  // 使用传入的 gameId（balldontlie eventId）
  const eventId = gameId;

  useEffect(() => {
    const fetchInjuries = async () => {
      if (!market?.teamA || !market?.teamB) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const data = await api.getGameData(eventId, market.teamA, market.teamB);
        setInjuriesData(data?.injuries || []);
      } catch {
        // Ignore errors
      } finally {
        setLoading(false);
      }
    };

    fetchInjuries();
  }, [eventId, market?.teamA, market?.teamB]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <FirstAidKit size={48} weight="duotone" className="mx-auto mb-3 text-negative animate-pulse" />
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  // 检查是否有伤病数据
  const hasInjuries = injuriesData.some(team => team.injuries?.length > 0);

  if (!hasInjuries) {
    return (
      <div className="text-center py-8">
        <FirstAidKit size={48} weight="duotone" className="mx-auto mb-3 text-gray-500" />
        <div className="text-gray-400">{t('tabs.injuriesNotAvailable')}</div>
        <div className="text-sm text-gray-500 mt-2">{t('injuries.noInjuriesHint')}</div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('out') || s.includes('缺阵')) return 'text-negative bg-negative/20 border border-negative/30';
    if (s.includes('doubtful') || s.includes('存疑')) return 'text-accent-warm bg-accent-warm/15 border border-accent-warm/30';
    if (s.includes('questionable') || s.includes('待定')) return 'text-accent-warm bg-accent-warm/15 border border-accent-warm/30';
    if (s.includes('day-to-day') || s.includes('probable') || s.includes('可能')) return 'text-accent-warm bg-accent-warm/15 border border-accent-warm/30';
    return 'text-gray-400 bg-surface-highlight/60 border border-glass-border';
  };

  const renderInjuryCard = (injury, idx) => (
    <div key={idx} className="flex items-start gap-3 py-3 border-b border-glass-border last:border-0">
      <img 
        src={injury.headshot || `https://a.espncdn.com/combiner/i?img=/i/headshots/nophoto.png&w=96&h=70`}
        alt={injury.athleteName}
        className="w-10 h-10 rounded-full bg-surface-highlight/70 flex-shrink-0"
        onError={(e) => { e.target.src = 'https://a.espncdn.com/combiner/i?img=/i/headshots/nophoto.png&w=96&h=70'; }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="font-medium text-white truncate">{injury.athleteName}</div>
          <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${getStatusColor(injury.status)}`}>
            {injury.status}
          </span>
        </div>
        <div className="text-xs text-gray-500 mt-0.5">
          {injury.position} {injury.jersey && `#${injury.jersey}`}
        </div>
        {injury.details && (
          <div className="text-xs text-gray-400 mt-1 leading-relaxed">{injury.details}</div>
        )}
      </div>
    </div>
  );

  const renderTeamColumn = (teamData) => {
    const hasInjuries = teamData?.injuries?.length > 0;
    const teamName = teamData?.teamName || 'Unknown Team';
    const logo = getTeamLogo(teamName, 48);
    
    return (
      <div className="flex-1 min-w-0">
        {/* Team Header */}
        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-glass-border">
          <img 
            src={logo} 
            alt={teamName} 
            className="w-8 h-8 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <span className="font-semibold text-white uppercase tracking-wide text-sm">
            {teamName}
          </span>
        </div>
        
        {/* Scrollable Injuries List */}
        <div className="max-h-[350px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
          {hasInjuries ? (
            teamData.injuries.map((injury, idx) => renderInjuryCard(injury, idx))
          ) : (
            <div className="text-center py-8 text-gray-500 text-sm">
              {t('injuries.noInjuries')}
            </div>
          )}
        </div>
      </div>
    );
  };

  // 根据 market 中的队伍顺序确定主客场
  const getTeamData = () => {
    const teamA = market?.teamA?.toLowerCase();
    const teamB = market?.teamB?.toLowerCase();
    
    const teamAData = injuriesData.find(t => 
      teamA && t.teamName?.toLowerCase().includes(teamA.split(' ').pop())
    ) || injuriesData[0];
    
    const teamBData = injuriesData.find(t => 
      teamB && t.teamName?.toLowerCase().includes(teamB.split(' ').pop())
    ) || injuriesData[1];
    
    return { teamAData, teamBData };
  };

  const { teamAData, teamBData } = getTeamData();

  return (
    <div className="flex gap-6">
      {/* Left Team Column */}
      {renderTeamColumn(teamAData)}
      
      {/* Divider */}
      <div className="w-px bg-surface-highlight/60 self-stretch" />
      
      {/* Right Team Column */}
      {renderTeamColumn(teamBData)}
    </div>
  );
}

function StatsTab({ market, gameDetail, accessStatus, user, t }) {
  const [teamStats, setTeamStats] = useState({ home: null, away: null });
  const [loading, setLoading] = useState(false);
  const [localAccessStatus, setLocalAccessStatus] = useState({
    isAuthRequired: false,
    isMembershipRequired: false,
  });

  const homeTeam = gameDetail?.homeTeam;
  const awayTeam = gameDetail?.awayTeam;

  // 合并全局和本地的 accessStatus
  const effectiveAccessStatus = {
    isAuthRequired: accessStatus?.isAuthRequired || localAccessStatus.isAuthRequired,
    isMembershipRequired: accessStatus?.isMembershipRequired || localAccessStatus.isMembershipRequired,
  };

  useEffect(() => {
    const fetchStats = async () => {
      if (!homeTeam?.teamId || !awayTeam?.teamId) return;

      setLoading(true);

      try {
        const marketKey = market?.id || market?.marketId || null;
        const [homeStats, awayStats] = await Promise.all([
          api.getTeamAdvancedStats(homeTeam.teamId, marketKey).catch((err) => {
            if (err?.isAuthRequired) {
              setLocalAccessStatus((prev) => ({ ...prev, isAuthRequired: true }));
            }
            if (err?.isMembershipRequired) {
              setLocalAccessStatus((prev) => ({ ...prev, isMembershipRequired: true }));
            }
            return null;
          }),
          api.getTeamAdvancedStats(awayTeam.teamId, marketKey).catch((err) => {
            if (err?.isAuthRequired) {
              setLocalAccessStatus((prev) => ({ ...prev, isAuthRequired: true }));
            }
            if (err?.isMembershipRequired) {
              setLocalAccessStatus((prev) => ({ ...prev, isMembershipRequired: true }));
            }
            return null;
          }),
        ]);
        setTeamStats({ home: homeStats, away: awayStats });
      } catch (err) {
        console.error('Failed to fetch team stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [homeTeam?.teamId, awayTeam?.teamId]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <ChartLineUp size={48} weight="duotone" className="mx-auto mb-3 text-accent animate-pulse" />
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  // 检查权限状态
  if (effectiveAccessStatus.isAuthRequired) {
    return (
      <div className="text-center py-8">
        <Lock size={48} weight="duotone" className="mx-auto mb-3 text-accent" />
        <div className="text-accent">{t('auth.loginRequired', '请先登录')}</div>
      </div>
    );
  }

  if (effectiveAccessStatus.isMembershipRequired) {
    return (
      <div className="text-center py-8">
        <Crown size={48} weight="duotone" className="mx-auto mb-3 text-accent-warm" />
        <div className="text-accent-warm mb-2">{t('stats.membershipRequired', '高级数据对比需要会员权限')}</div>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('membership-required', { detail: { currentTier: user?.tier_name || 'free' } }))}
          className="mt-4 px-4 py-2 bg-accent-strong hover:bg-accent text-white rounded-lg text-sm transition-colors"
        >
          {t('membership.viewPlans', '查看会员方案')}
        </button>
      </div>
    );
  }

  if (!teamStats.home && !teamStats.away) {
    return (
      <div className="text-center py-8">
        <ChartLineUp size={48} weight="duotone" className="mx-auto mb-3 text-gray-500" />
        <div className="text-gray-400">{t('tabs.statsNotAvailable', '暂无数据对比')}</div>
      </div>
    );
  }

  // 统计项配置 - 映射到 API 返回的 stat name
  const statItems = [
    { key: 'avgPoints', label: t('stats.ppg'), format: (v) => v?.toFixed(1) || '-', section: 'offensive' },
    { key: 'avgAssists', label: t('stats.apg'), format: (v) => v?.toFixed(1) || '-', section: 'offensive' },
    { key: 'avgRebounds', label: t('stats.rpg'), format: (v) => v?.toFixed(1) || '-', section: 'general' },
    { key: 'fieldGoalPct', label: t('stats.fgPct'), format: (v) => v ? `${v.toFixed(1)}%` : '-', section: 'offensive' },
    { key: 'threePointPct', label: t('stats.threePct'), format: (v) => v ? `${v.toFixed(1)}%` : '-', section: 'offensive' },
    { key: 'freeThrowPct', label: t('stats.ftPct'), format: (v) => v ? `${v.toFixed(1)}%` : '-', section: 'offensive' },
    { key: 'avgSteals', label: t('stats.spg'), format: (v) => v?.toFixed(1) || '-', section: 'defensive' },
    { key: 'avgBlocks', label: t('stats.bpg'), format: (v) => v?.toFixed(1) || '-', section: 'defensive' },
  ];

  // 从 allStats 中获取指定统计项的值
  const getStatValue = (stats, key, section) => {
    if (!stats?.allStats) return null;
    
    // 根据 section 获取对应的 stats 数组
    const sectionData = stats.allStats[section];
    if (!sectionData?.stats) return null;
    
    // 在 stats 数组中查找匹配的 stat
    const stat = sectionData.stats.find(s => s.name === key);
    return stat?.value ?? null;
  };

  const compareStats = (homeVal, awayVal) => {
    if (homeVal == null || awayVal == null) return 'equal';
    if (homeVal > awayVal) return 'home';
    if (awayVal > homeVal) return 'away';
    return 'equal';
  };

  return (
    <div className="space-y-4">
      {/* 表头 - Away team (teamA) 在左边，Home team (teamB) 在右边，与顶部一致 */}
      <div className="grid grid-cols-3 gap-4 text-center text-sm font-medium">
        <div className="flex items-center justify-center gap-2">
          <img 
            src={`https://a.espncdn.com/i/teamlogos/nba/500/${awayTeam?.teamAbbr?.toLowerCase()}.png`}
            alt={awayTeam?.teamName}
            className="w-6 h-6"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <span className="text-accent-warm">{awayTeam?.teamAbbr || market?.teamA}</span>
        </div>
        <div className="text-gray-500">{t('stats.stat')}</div>
        <div className="flex items-center justify-center gap-2">
          <span className="text-accent">{homeTeam?.teamAbbr || market?.teamB}</span>
          <img 
            src={`https://a.espncdn.com/i/teamlogos/nba/500/${homeTeam?.teamAbbr?.toLowerCase()}.png`}
            alt={homeTeam?.teamName}
            className="w-6 h-6"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      </div>

      {/* 统计对比 - Away 在左边，Home 在右边 */}
      <div className="space-y-2">
        {statItems.map(({ key, label, format, section }) => {
          const awayVal = getStatValue(teamStats.away, key, section);
          const homeVal = getStatValue(teamStats.home, key, section);
          const winner = compareStats(awayVal, homeVal);
          
          return (
            <div key={key} className="grid grid-cols-3 gap-4 py-2 bg-surface-highlight/30 rounded-lg">
              <div className={`text-center font-medium ${winner === 'home' ? 'text-positive' : 'text-gray-300'}`}>
                {format(awayVal)}
              </div>
              <div className="text-center text-gray-500 text-sm">{label}</div>
              <div className={`text-center font-medium ${winner === 'away' ? 'text-positive' : 'text-gray-300'}`}>
                {format(homeVal)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
